import { useState, useEffect, Suspense, lazy } from 'react';
import { useApp } from '../context/AppContext';
import useFullscreen from '../hooks/useFullscreen';
import { useCurrency } from '../hooks/useCurrency';
// import VoiceSettings from './VoiceSettings'; // voice disabled
import AccessibilitySettings from './AccessibilitySettings';
import SoundSettings from './SoundSettings';
import { API_BASE } from '../config';
import { useI18n } from '../context/I18nContext';
import { THEMES, applyTheme, applyChatTransparency } from '../themes';
import SpinningLogo from './SpinningLogo';
import SkillSelectorModal from './SkillSelectorModal';

const ConnectionLogPanel = lazy(() => import('./ConnectionLogPanel'));
const VoiceSelector = lazy(() => import('./VoiceSelector'));
const PricingPanel = lazy(() => import('./PricingPanel'));
const CharterPanel = lazy(() => import('./CharterPanel'));

const POWER_CODE = 'cogency-pro';

const DOMAINS_LIST = [
  { key: 'research', labelKey: 'domainResearch', icon: '🔬' },
  { key: 'mechanics', labelKey: 'domainMechanics', icon: '⚙️' },
  { key: 'biology', labelKey: 'domainBiology', icon: '🧬' },
  { key: 'agriculture', labelKey: 'domainAgriculture', icon: '🌾' },
  { key: 'medicine', labelKey: 'domainMedicine', icon: '🩺' },
  { key: 'it', labelKey: 'domainIT', icon: '💻' },
  { key: 'law', labelKey: 'domainLaw', icon: '⚖️' },
  { key: 'economy', labelKey: 'domainEconomy', icon: '📊' },
  { key: 'education', labelKey: 'domainEducation', icon: '🎓' },
  { key: 'energy', labelKey: 'domainEnergy', icon: '⚡' },
  { key: 'environment', labelKey: 'domainEnvironment', icon: '🌍' },
  { key: 'construction', labelKey: 'domainConstruction', icon: '🏗️' },
  { key: 'finance', labelKey: 'domainFinance', icon: '🏦' },
  { key: 'marketing', labelKey: 'domainMarketing', icon: '📢' },
  { key: 'hr', labelKey: 'domainHR', icon: '👥' },
  { key: 'cybersecurity', labelKey: 'domainCybersecurity', icon: '🔒' },
  { key: 'logistics', labelKey: 'domainLogistics', icon: '🚚' },
  { key: 'arts', labelKey: 'domainArts', icon: '🎨' },
  { key: 'publichealth', labelKey: 'domainPublicHealth', icon: '🏥' },
  { key: 'mining', labelKey: 'domainMining', icon: '⛏️' },
  { key: 'oilgas', labelKey: 'domainOilGas', icon: '🛢️' },
  { key: 'telecom', labelKey: 'domainTelecom', icon: '📡' },
  { key: 'robotics', labelKey: 'domainRobotics', icon: '🤖' },
  { key: 'psychology', labelKey: 'domainPsychology', icon: '🧠' },
  { key: 'philosophy', labelKey: 'domainPhilosophy', icon: '📜' },
  { key: 'linguistics', labelKey: 'domainLinguistics', icon: '🗣️' },
  { key: 'space', labelKey: 'domainSpace', icon: '🚀' },
  { key: 'climate', labelKey: 'domainClimate', icon: '🌤️' },
];

const PROVIDER_LABELS = {
  ollama: { label: 'Local', color: '#10b981' },
  claude: { label: 'Claude', color: '#d946ef' },
  kimi: { label: 'Kimi', color: '#f59e0b' },
  hf: { label: 'HF', color: '#f97316' },
  tchadgpt: { label: 'TchadGPT', color: '#002664' },
  zai: { label: 'Z.ai', color: '#3b82f6' },
  deepseek: { label: 'DeepSeek', color: '#4f46e5' },
  xai: { label: 'Grok', color: '#f97316' },
  chatlyai: { label: 'ChatlyAI', color: '#ec4899' }
};

export default function SetupScreen({ isAdmin, isSuperAdmin }) {
  const {
    connected, models, personas, providers, providersReady, agents, synthModel, rounds, debateMode, setDebateMode,
    setupError, setupLoading, setScreen, setSynthModel, setRounds, setAgents,
    addAgent, removeAgent, updateAgentPersona, updateAgentModel,
    saveSession, showInvite, setShowInvite, setShowCharter, setShowThemeSelector, setShowPricing, validateKey, checkSetup, logout, user, fetchPendingCount, canConfigure, updateUserSkill,
    selectedDomains, setSelectedDomains
  } = useApp();
  const { t, lang, switchLang } = useI18n();
  const { formatPriceUSD, symbol, currency, loading: currencyLoading } = useCurrency();

  const [activeTab, setActiveTab] = useState('status');
  const [menuOpen, setMenuOpen] = useState(false);
  const { toggle: toggleFullscreen } = useFullscreen();
  const [isPowerUser, setIsPowerUser] = useState(() => localStorage.getItem('cogency_power_user') === 'true');
  const [showUnlock, setShowUnlock] = useState(false);
  const [unlockInput, setUnlockInput] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminSubTab, setAdminSubTab] = useState('users');
  const [userSort, setUserSort] = useState('name');
  const [settingsSubTab, setSettingsSubTab] = useState('sound');
  const [userSkill, setUserSkill] = useState(user?.skill || null);
  const [showSkillModal, setShowSkillModal] = useState(!user?.skill);
  const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('cogency_theme') || 'default');
  const [chatTransparent, setChatTransparent] = useState(() => localStorage.getItem('cogency_chat_transparent') === 'true');
  const [modelGrowth, setModelGrowth] = useState(null);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyUserIds, setNotifyUserIds] = useState([]);
  const [notifySending, setNotifySending] = useState(false);
  const [elevenLabsEnabled, setElevenLabsEnabled] = useState(false);
  const [elevenLabsLoading, setElevenLabsLoading] = useState(false);
  const [bannerConfig, setBannerConfig] = useState({
    enabled: false,
    message: '',
    showWhenExpired: true,
    showWhenActive: false,
    backgroundColor: '#ef4444',
    textColor: '#ffffff'
  });
  const [bannerLoading, setBannerLoading] = useState(false);

  const handleSkillSelect = async (skill) => {
    // Immediate feedback: close modal and update local state right away
    setUserSkill(skill);
    setShowSkillModal(false);

    if (skill === 'beginner') {
      setRounds(5);
      setAgents([
        { persona: 'claude', model: 'claude:claude-opus-4-7' },
        { persona: 'kimi', model: 'kimi:kimi-k2.6' },
        { persona: 'grok', model: 'xai:grok-3-latest' }
      ]);
      setSynthModel('deepseek:deepseek-reasoner');
      setDebateMode('sequential');
      setScreen('main');
    }

    // Persist to backend in the background
    try {
      await updateUserSkill(skill);
    } catch {
      // Silent fail; local state is already updated for UX responsiveness
    }
  };

  // Theme selection helpers for inline display in Settings > Interface
  const handleThemeSelect = (key) => {
    applyTheme(key);
    localStorage.setItem('cogency_theme', key);
    setCurrentTheme(key);
  };

  const handleToggleTransparency = (e) => {
    const val = e.target.checked;
    setChatTransparent(val);
    applyChatTransparency(val);
    localStorage.setItem('cogency_chat_transparent', val ? 'true' : 'false');
  };

  const getThemeName = (theme) => {
    if (lang === 'ar') return theme.nameAr;
    if (lang === 'en') return theme.nameEn;
    return theme.name;
  };
  const [bannerMessage, setBannerMessage] = useState('');
  const [financialData, setFinancialData] = useState(null);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialDays, setFinancialDays] = useState(30);
  const [reportLanguage, setReportLanguage] = useState(lang || 'fr');
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pendingRoles, setPendingRoles] = useState({});
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [actionSelect, setActionSelect] = useState({});
  const [editUserId, setEditUserId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordCurrent, setChangePasswordCurrent] = useState('');
  const [changePasswordNew, setChangePasswordNew] = useState('');
  const [changePasswordConfirm, setChangePasswordConfirm] = useState('');
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const DOMAINS = DOMAINS_LIST;

  const maxAgents = Math.min(20, personas.length);

  const hasLocal = providers.ollama?.available === true || providers.ollama?.connected === true;
  const hasCloud = providers.claude?.available || providers.kimi?.available || providers.openrouter?.available || providers.hf?.available || providers.tchadgpt?.available || providers.zai?.available || providers.deepseek?.available || providers.xai?.available || providers.chatlyai?.available;
  const connectedOk = providersReady;

  const [providerValidity, setProviderValidity] = useState({});

  useEffect(() => {
    const validateProviders = async () => {
      try {
        const token = localStorage.getItem('cogency_token');
        const res = await fetch(`${API_BASE}/api/validate-providers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setProviderValidity(data.results || {});
      } catch (e) {
        console.error('[ValidateProviders]', e);
      }
    };
    validateProviders();
  }, []);

  const isModelReady = (modelId) => {
    if (!modelId) return false;
    const model = models.find(m => m.id === modelId);
    if (!model) return false;
    if (model.provider === 'ollama') return hasLocal;
    return providers[model.provider]?.available === true;
  };

  const isAgentReady = (agent) => {
    if (!agent?.model) return false;
    return isModelReady(agent.model);
  };

  const allAgentsReady = agents.every(isAgentReady);
  const synthReady = isModelReady(synthModel);
  const canStart = allAgentsReady && synthReady;

  const handleUnlock = () => {
    if (unlockInput.trim() === POWER_CODE) {
      setIsPowerUser(true);
      localStorage.setItem('cogency_power_user', 'true');
      setShowUnlock(false);
      setUnlockError('');
    } else {
      setUnlockError('Incorrect unlock code.');
    }
  };

  const fetchUsers = async () => {
    if (!isAdmin) return;
    setAdminLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` }
      });
      const data = await res.json();
      const list = data?.users || data;
      setAllUsers(Array.isArray(list) ? list : []);
    } catch {
      setAllUsers([]);
    } finally {
      setAdminLoading(false);
    }
  };

  const verifyUser = async (userId, role = 'subscriber') => {
    try {
      const res = await fetch(`${API_BASE}/api/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ userId, role })
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
        fetchPendingCount();
      }
    } catch { /* ignore */ }
  };

  const rejectUser = async (userId) => {
    try {
      const res = await fetch(`${API_BASE}/api/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ userId })
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
        fetchPendingCount();
      }
    } catch { /* ignore */ }
  };

  const changeRole = async (userId, role) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ role })
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
        fetchPendingCount();
      }
    } catch { /* ignore */ }
  };

  const deleteUser = async (userId) => {
    if (!confirm(t('confirmDeleteUser'))) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` }
      });
      const data = await res.json();
      if (data.success) fetchUsers();
    } catch { /* ignore */ }
  };

  const suspendUser = async (userId) => {
    if (!confirm(t('confirmSuspendUser'))) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/suspend`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` }
      });
      const data = await res.json();
      if (data.success) fetchUsers();
    } catch { /* ignore */ }
  };

  const activateUser = async (userId) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/activate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` }
      });
      const data = await res.json();
      if (data.success) fetchUsers();
    } catch { /* ignore */ }
  };

  const ejectUser = async (userId) => {
    if (!confirm(t('confirmEjectUser'))) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/eject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` }
      });
      const data = await res.json();
      if (data.success) fetchUsers();
    } catch { /* ignore */ }
  };

  const changePassword = async () => {
    setChangePasswordError('');
    setChangePasswordSuccess('');
    if (!changePasswordCurrent || !changePasswordNew || !changePasswordConfirm) {
      setChangePasswordError(t('fillAllFields') || 'Veuillez remplir tous les champs');
      return;
    }
    if (changePasswordNew.length < 6) {
      setChangePasswordError(t('passwordTooShort') || 'Mot de passe trop court');
      return;
    }
    if (changePasswordNew !== changePasswordConfirm) {
      setChangePasswordError(t('passwordsDoNotMatch') || 'Les mots de passe ne correspondent pas');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ currentPassword: changePasswordCurrent, newPassword: changePasswordNew })
      });
      const data = await res.json();
      if (data.success) {
        setChangePasswordSuccess(t('passwordChangedSuccess') || 'Mot de passe modifié avec succès');
        setChangePasswordCurrent('');
        setChangePasswordNew('');
        setChangePasswordConfirm('');
        setTimeout(() => {
          setShowChangePassword(false);
          setChangePasswordSuccess('');
        }, 2000);
      } else {
        setChangePasswordError(data.error || t('passwordChangeFailed') || 'Échec');
      }
    } catch {
      setChangePasswordError(t('passwordChangeFailed') || 'Échec');
    }
  };

  const resetPassword = async (userId) => {
    setResetError('');
    setResetSuccess('');
    if (!resetPasswordValue || resetPasswordValue.length < 6) {
      setResetError(t('passwordTooShort'));
      return;
    }
    if (resetPasswordValue !== resetConfirmPassword) {
      setResetError(t('passwordsDoNotMatch'));
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ password: resetPasswordValue })
      });
      const data = await res.json();
      if (data.success) {
        setResetSuccess(t('passwordResetSuccess'));
        setResetPasswordValue('');
        setResetConfirmPassword('');
        setTimeout(() => {
          setResetUserId(null);
          setResetSuccess('');
        }, 2000);
      } else {
        setResetError(data.error || t('passwordResetFailed'));
      }
    } catch {
      setResetError(t('passwordResetFailed'));
    }
  };

  const fetchModelGrowth = async () => {
    setGrowthLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/model-growth`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` }
      });
      const data = await res.json();
      setModelGrowth(data);
    } catch {
      setModelGrowth(null);
    } finally {
      setGrowthLoading(false);
    }
  };

  const sendNotification = async () => {
    if (!notifyMessage.trim()) return;
    setNotifySending(true);
    try {
      const targetIds = notifyUserIds.includes('all') ? allUsers.map(u => u.id) : notifyUserIds;
      const res = await fetch(`${API_BASE}/api/admin/notify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ userIds: targetIds, title: notifyTitle, message: notifyMessage })
      });
      const data = await res.json();
      if (data.success) {
        alert(`${t('notificationSent')}: ${data.notifiedCount} ${t('users') || 'users'}`);
        setNotifyTitle('');
        setNotifyMessage('');
        setNotifyUserIds([]);
      }
    } catch { /* ignore */ }
    setNotifySending(false);
  };

  const editUser = async (userId) => {
    setEditLoading(true);
    setEditError('');
    setEditSuccess('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('cogency_token')}` },
        body: JSON.stringify({ name: editName.trim(), email: editEmail.trim() })
      });
      if (!res.ok) {
        const text = await res.text();
        setEditError(`Erreur ${res.status}: ${text.slice(0, 200)}`);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setEditSuccess(t('success') || 'Utilisateur modifié');
        setTimeout(() => { setEditUserId(null); setEditName(''); setEditEmail(''); fetchUsers(); }, 1500);
      } else {
        setEditError(data.error || 'Erreur');
      }
    } catch (err) { setEditError('Erreur réseau: ' + (err.message || '')); }
    setEditLoading(false);
  };

  const openEditUser = (user) => {
    setEditUserId(user.id);
    setEditName(user.name || '');
    setEditEmail(user.email || '');
    setEditError('');
    setEditSuccess('');
  };

  const applyBannerToUser = async (userId) => {
    if (!confirm(t('applyBannerConfirm') || 'Appliquer la bannière à cet utilisateur ?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${userId}/trial-banner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ enabled: true })
      });
      const data = await res.json();
      if (data.success) {
        alert(t('bannerApplied') || 'Bannière appliquée avec succès');
      } else {
        alert(data.error || (t('failedToApplyBanner') || 'Erreur'));
      }
    } catch {
      alert(t('failedToApplyBanner') || 'Erreur');
    }
  };

  const notifyUser = async (userId) => {
    const title = prompt(t('notificationTitle') || 'Titre de la notification :');
    const message = prompt(t('notificationMessage') || 'Message :');
    if (!message) return;

    setNotifySending(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/notify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ userIds: [userId], title: title || '', message })
      });
      const data = await res.json();
      if (data.success) {
        alert(t('notificationSent') || 'Notification envoyée');
      } else {
        alert(data.error || (t('failedToSendNotification') || 'Erreur'));
      }
    } catch {
      alert(t('failedToSendNotification') || 'Erreur');
    }
    setNotifySending(false);
  };

  const fetchElevenLabsStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/elevenlabs-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` }
      });
      const data = await res.json();
      setElevenLabsEnabled(data.enabled === true);
    } catch { /* ignore */ }
  };

  const toggleElevenLabs = async () => {
    setElevenLabsLoading(true);
    try {
      const next = !elevenLabsEnabled;
      const res = await fetch(`${API_BASE}/api/admin/elevenlabs-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ enabled: next })
      });
      const data = await res.json();
      setElevenLabsEnabled(data.enabled === true);
    } catch { /* ignore */ }
    setElevenLabsLoading(false);
  };

  const fetchBannerConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/app-config`);
      const data = await res.json();
      if (data?.trialBanner) {
        setBannerConfig(prev => ({ ...prev, ...data.trialBanner }));
      }
    } catch { /* ignore */ }
  };

  const saveBannerConfig = async () => {
    setBannerLoading(true);
    setBannerMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/app-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('cogency_token')}`
        },
        body: JSON.stringify({ trialBanner: bannerConfig })
      });
      const data = await res.json();
      if (data.success) {
        setBannerMessage(t('settingsSavedSuccessfully') || 'Settings saved successfully');
        if (data.config?.trialBanner) {
          setBannerConfig(prev => ({ ...prev, ...data.config.trialBanner }));
        }
      } else {
        setBannerMessage(t('failedToSaveSettings') || 'Failed to save settings');
      }
    } catch {
      setBannerMessage(t('failedToSaveSettings') || 'Failed to save settings');
    }
    setBannerLoading(false);
    setTimeout(() => setBannerMessage(''), 3000);
  };

  const fetchFinancialData = async () => {
    setFinancialLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/usage/summary?days=${financialDays}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` }
      });
      const data = await res.json();
      setFinancialData(data);
    } catch {
      // ignore
    }
    setFinancialLoading(false);
  };

  const exportFinancialPDF = async () => {
    console.log('[PDF] Export function called, financialData exists:', !!financialData);
    if (!financialData) return;

    const btn = document.getElementById('export-pdf-btn');
    const originalText = btn?.textContent;
    if (btn) {
      btn.textContent = t('generating') || 'Génération…';
      btn.disabled = true;
    }

    try {
      const days = financialDays || 30;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);

      const response = await fetch(`${API_BASE}/api/admin/financial-report-pdf?days=${days}&lang=${reportLanguage || 'fr'}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || 'PDF generation failed on server');
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const error = await response.json();
        throw new Error(error.error || 'PDF generation failed');
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        throw new Error('Generated PDF file is empty');
      }

      const contentDisposition = response.headers.get('content-disposition');
      let filename = `financial-report-${days}days-${new Date().toISOString().slice(0, 10)}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch && filenameMatch[1]) filename = filenameMatch[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);

      try {
        a.click();
      } catch (e) {
        console.warn('Anchor click failed, trying window.open:', e);
        window.open(url, '_blank');
      }

      // Keep the object URL alive long enough for the browser to start the download
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        if (a.parentNode) a.parentNode.removeChild(a);
      }, 3000);

    } catch (error) {
      console.error('PDF export error:', error);
      if (error.name === 'AbortError') {
        alert(t('pdfExportTimeout') || 'Le téléchargement du PDF a dépassé le délai. Veuillez réessayer avec une période plus courte.');
      } else {
        alert(t('pdfExportFailed') || 'Échec de l\'export PDF. Erreur : ' + (error.message || String(error)));
      }
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = originalText || (t('export') || 'Export');
      }
    }
  };

  const openFinancialPDFPreview = async () => {
    if (!financialData) return;
    setPdfPreviewLoading(true);
    setPdfPreviewOpen(true);
    try {
      const days = financialDays || 30;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000);
      const response = await fetch(`${API_BASE}/api/admin/financial-report-pdf?days=${days}&lang=${reportLanguage || 'fr'}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('cogency_token')}` },
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || 'PDF generation failed on server');
      }
      const blob = await response.blob();
      if (blob.size === 0) throw new Error('Generated PDF file is empty');
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
    } catch (error) {
      console.error('PDF preview error:', error);
      alert(t('pdfPreviewFailed') || 'Échec de l\'aperçu PDF. Erreur : ' + (error.message || String(error)));
      setPdfPreviewOpen(false);
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const closeFinancialPDFPreview = () => {
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
    setPdfPreviewOpen(false);
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchFinancialData();
    }
  }, [isSuperAdmin, financialDays]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
    if (isSuperAdmin) {
      fetchElevenLabsStatus();
    }
    if (canConfigure) {
      fetchBannerConfig();
    }
  }, [isAdmin, isSuperAdmin, canConfigure]);

  const tabs = [
    { key: 'status', label: t('status'), icon: '🔌' },
    { key: 'agents', label: t('agents'), icon: '🤖' },
    { key: 'settings', label: t('settings'), icon: '⚙️' },
    { key: 'accessibility', label: t('accessibility') || 'Accessibilité', icon: '♿' },
    { key: 'pricing', label: t('pricing') || 'Tarification', icon: '💎' },
    { key: 'charter', label: t('viewCharter') || 'Charte', icon: '📜' },
    ...(isAdmin ? [
      { key: 'admin', label: t('admin'), icon: '👤' },
      { key: 'history', label: t('history') || t('sessionHistory') || 'Historique', icon: '📜' }
    ] : [
      { key: 'history', label: t('history') || t('sessionHistory') || 'Historique', icon: '📜' }
    ]),
    ...(isSuperAdmin ? [
      { key: 'financial', label: t('financial') || 'Financial', icon: '💰' }
    ] : [])
  ];

  return (
    <>
      {showSkillModal && (
        <SkillSelectorModal onSelect={handleSkillSelect} />
      )}
      {!setupLoading && !providersReady && (
        <div className="setup-error providers-not-ready-banner" style={{ margin: '12px auto', maxWidth: '900px' }}>
          <div className="error-icon">⚠️</div>
          <div>
            <h3>{t('noProvidersReadyTitle') || 'Aucun provider IA n\'est disponible'}</h3>
            <p>{t('noProvidersReadyDetail') || 'Vérifiez vos crédits ou qu\'Ollama est démarré, puis rafraîchissez.'}</p>
          </div>
          <button className="btn-secondary" onClick={checkSetup}>{t('tryAgain') || 'Réessayer'}</button>
        </div>
      )}
      <div className="setup-screen">
        <div className="setup-card">
        <div className="setup-header-sticky">
          <div className="setup-header-compact">
            <div className="setup-header-brand">
              <SpinningLogo size={110} className="brand-logo" />
              <div className="brand-text">
                <div className="brand-title">Cogency V1.0</div>
                <div className="brand-tagline-row">
                  <div className="brand-tagline">{t('brandTagline')}</div>
                  <div className="brand-connected-as">
                    {t('connectedAs')} {user?.name || user?.email || ''} ({user?.email || ''})
                  </div>
                </div>
              </div>
            </div>
            <div className="setup-header-actions">
              <button
                className="mobile-menu-toggle"
                onClick={() => setMenuOpen(o => !o)}
                aria-label={t('menu') || 'Menu'}
                aria-expanded={menuOpen}
              >
                {menuOpen ? '✕' : '☰'}
              </button>
              <div className={`setup-header-actions-inner ${menuOpen ? 'open' : ''}`}>
                <button className="btn-ghost btn-small logout-btn" onClick={logout} aria-label={t('logout')}>{t('logout')}</button>
              </div>
            </div>
          </div>

          <div className="setup-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`setup-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => {
                if (tab.key === 'history') {
                  setScreen('history');
                } else {
                  setActiveTab(tab.key);
                }
              }}
            >
              {tab.icon && <span className="tab-icon">{tab.icon}</span>}
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>
        </div>

        <div className="models-ribbon">
          <div className="models-ribbon-track">
            <div className="models-ribbon-content">
              {setupLoading ? <span className="loading-dots">{t('loading')}</span> :
                models.map(m => {
                  const p = PROVIDER_LABELS[m.provider] || { label: m.provider, color: '#666' };
                  return <span key={`ribbon-${m.id}`} className={`model-chip ${m.provider !== 'ollama' ? 'cloud' : ''}`} style={{ borderColor: p.color, color: p.color }}>{p.label}: {m.name}</span>;
                })
              }
            </div>
            <div className="models-ribbon-content" aria-hidden="true">
              {setupLoading ? <span className="loading-dots">{t('loading')}</span> :
                models.map(m => {
                  const p = PROVIDER_LABELS[m.provider] || { label: m.provider, color: '#666' };
                  return <span key={`ribbon-dup-${m.id}`} className={`model-chip ${m.provider !== 'ollama' ? 'cloud' : ''}`} style={{ borderColor: p.color, color: p.color }}>{p.label}: {m.name}</span>;
                })
              }
            </div>
          </div>
        </div>

        <div className="setup-tab-content">
          {userSkill === 'expert' ? (
            <div className="tab-panel skill-placeholder-panel">
              <h3>🚧 {t('underDevelopment') || 'Under Development'}</h3>
            </div>
          ) : (
          <>
          {activeTab === 'status' && (
            <div className="tab-panel status-panel">
              {!setupLoading && !connectedOk && (
                <div className="setup-error">
                  <div className="error-icon">⚠️</div>
                  <h3>{t('connectionFailed')}</h3>
                  <p>{setupError || t('noProvidersAvailableDetail')}</p>
                  <button className="btn-secondary" onClick={checkSetup}>{t('tryAgain')}</button>
                </div>
              )}

              <div className="domain-box">
                    <h3 className="domain-title">{t('applicationDomain')}</h3>
                    <div className="domain-buttons">
                      <button
                        key="free"
                        className={`domain-btn ${selectedDomains.length === 0 ? 'active' : ''}`}
                        onClick={() => setSelectedDomains([])}
                      >
                        <span className="domain-icon">🔓</span>
                        <span className="domain-label">{t('freeSession')}</span>
                      </button>
                      {DOMAINS.map(d => (
                        <button
                          key={d.key}
                          className={`domain-btn ${selectedDomains.includes(d.key) ? 'active' : ''}`}
                          onClick={() => {
                            setSelectedDomains(prev =>
                              prev.includes(d.key)
                                ? prev.filter(k => k !== d.key)
                                : [...prev, d.key]
                            );
                          }}
                        >
                          <span className="domain-icon">{d.icon}</span>
                          <span className="domain-label">{t(d.labelKey)}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {!setupLoading && connectedOk && (
                    <>
                      {!canStart && (
                        <div className="setup-error" style={{ marginBottom: '16px' }}>
                          <div className="error-icon">⚠️</div>
                          <div>
                            <h3>{t('cannotStartTitle') || 'Configuration incomplète'}</h3>
                            <p>{t('cannotStartDetail') || 'Certains agents utilisent des modèles non disponibles. Vérifiez la configuration de vos agents ou changez de modèle.'}</p>
                          </div>
                        </div>
                      )}
                      <div className="setup-actions">
                        <button className="btn-primary btn-large" onClick={() => setScreen('main')} disabled={!canStart}>
                          {t('startCollaborating') || 'Démarrer la collaboration'}
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </button>
                        <button className="btn-secondary btn-large" onClick={() => { setScreen('main'); setTimeout(saveSession, 300); }} disabled={!canStart}>
                          🔗 {t('startSharedSession') || 'Démarrer une session partagée'}
                        </button>
                        <p className="setup-actions-hint">{t('sharedSessionHint') || 'Les sessions partagées permettent à d\'autres personnes de suivre et collaborer en temps réel.'}</p>
                      </div>
                    </>
                  )}

                </div>
          )}

          {activeTab === 'agents' && (
            <div className="tab-panel agents-panel">
              <div className="agents-panel-header">
                <h3>{t('yourAgents')}</h3>
                {canConfigure && (
                  <div className="agent-actions">
                    <button className="btn-secondary btn-small" onClick={addAgent} disabled={agents.length >= maxAgents} aria-label={t('addAgent')} title={agents.length >= maxAgents ? t('agentLimitReached') || `Limite atteinte (${maxAgents})` : undefined}>+ {t('addAgent')}</button>
                    <span className="agent-limit">{agents.length}/{maxAgents} {t('agents').toLowerCase()}</span>
                  </div>
                )}
              </div>

              <div className="debate-mode-card">
                <div className="debate-mode-header">
                  <span className="debate-mode-icon">⚙️</span>
                  <div>
                    <div className="debate-mode-title">{t('debateMode')}</div>
                    <div className="debate-mode-desc">{debateMode === 'sequential' ? t('sequentialModeDesc') : t('parallelModeDesc')}</div>
                  </div>
                </div>
                <div className="debate-mode-toggle">
                  <button
                    className={`debate-mode-btn ${debateMode === 'sequential' ? 'active' : ''}`}
                    onClick={() => setDebateMode('sequential')}
                    disabled={!canConfigure}
                    title={t('sequentialModeDesc')}
                  >
                    ⏱️ {t('sequentialMode')}
                  </button>
                  <button
                    className={`debate-mode-btn ${debateMode === 'parallel' ? 'active' : ''}`}
                    onClick={() => setDebateMode('parallel')}
                    disabled={!canConfigure}
                    title={t('parallelModeDesc')}
                  >
                    ⚡ {t('parallelMode')}
                  </button>
                </div>
              </div>

              <div className="agent-config-card synth-card" style={{ '--agent-color': '#8b5cf6' }}>
                <div className="agent-config-header">
                  <span className="agent-icon">✨</span>
                  <div>
                    <div className="agent-config-name">{t('synthesizer')}</div>
                    <div className="agent-config-role">{t('finalAnswer')}</div>
                  </div>
                </div>
                <div className="agent-fields">
                  <div className="field-group">
                    <label>{t('model')}</label>
                    <select value={synthModel} onChange={e => setSynthModel(e.target.value)} disabled={!canConfigure}>
                      {models.map(m => {
                        const pl = PROVIDER_LABELS[m.provider] || { label: m.provider };
                        return <option key={m.id} value={m.id}>{pl.label}: {m.name}</option>;
                      })}
                    </select>
                  </div>
                  <div className="field-group">
                    <label>{t('discussionRounds')}</label>
                    <select value={rounds} onChange={e => setRounds(parseInt(e.target.value))} disabled={!canConfigure}>
                      <option value={2}>2</option>
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={30}>30</option>
                      <option value={35}>35</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={-1}>∞</option>
                    </select>
                  </div>
                </div>
                <p className="agent-desc">{t('combinesAllAgentArguments')}</p>
              </div>

              <div className="agent-config-grid">
                {agents.filter(Boolean).filter(isAgentReady).map((agent, idx) => {
                  const persona = personas.find(p => p.id === agent.persona) || personas[0];
                  const color = persona?.color || '#3b82f6';
                  const isPremium = persona && !persona.available;
                  return (
                    <div key={idx} className="agent-config-card" style={{ '--agent-color': color }}>
                      <div className="agent-config-header">
                        <span className="agent-icon">{persona?.icon || '🔬'}</span>
                        <div>
                          <div className="agent-number">{t('agent')} {idx + 1}</div>
                          <div className="agent-config-name">{persona?.name || 'Agent'}</div>
                          <div className="agent-config-role" style={{ color }}>{persona?.role || ''}</div>
                        </div>
                        {canConfigure && agents.length > 2 && <button className="agent-remove" onClick={() => removeAgent(idx)} aria-label={t('remove')}>{t('remove')}</button>}
                      </div>
                      {canConfigure ? (
                        <div className="agent-fields">
                          <div className="field-group">
                            <label>{t('persona')}</label>
                            <select value={agent.persona} onChange={e => updateAgentPersona(idx, e.target.value)}>
                              {personas.map(p => <option key={p.id} value={p.id}>{p.icon} {p.name} — {p.role}</option>)}
                            </select>
                          </div>
                          <div className="field-group">
                            <label>{t('model')}</label>
                            <select value={agent.model} onChange={e => updateAgentModel(idx, e.target.value)} disabled={isPremium}>
                              {isPremium ? (
                                <option value="">{t('premiumPersonaMessage')}</option>
                              ) : (
                                models.map(m => {
                                  const pl = PROVIDER_LABELS[m.provider] || { label: m.provider };
                                  return <option key={m.id} value={m.id}>{pl.label}: {m.name}</option>;
                                })
                              )}
                            </select>
                          </div>
                        </div>
                      ) : (
                        <div className="agent-readonly" style={{ padding: '10px 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                          <div><strong>{t('persona')}:</strong> {persona?.name}</div>
                          <div><strong>{t('model')}:</strong> {(agent.model || '').split(':').pop() || (isPremium ? t('premiumPersonaMessage') : '-')}</div>
                        </div>
                      )}
                      {isPremium ? (
                        <p className="agent-desc premium-persona-message" style={{ color: '#f59e0b', fontWeight: 500 }}>
                          ⭐ {t('premiumPersonaMessage')}
                        </p>
                      ) : (
                        <p className="agent-desc">{(persona?.persona || '').split('\n')[0]}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {!setupLoading && connectedOk && (
                <>
                  {!canStart && (
                    <div className="setup-error" style={{ marginBottom: '16px', marginTop: '28px' }}>
                      <div className="error-icon">⚠️</div>
                      <div>
                        <h3>{t('cannotStartTitle') || 'Configuration incomplète'}</h3>
                        <p>{t('cannotStartDetail') || 'Certains agents utilisent des modèles non disponibles. Vérifiez la configuration de vos agents ou changez de modèle.'}</p>
                      </div>
                    </div>
                  )}
                  <div className="setup-actions" style={{ marginTop: '28px' }}>
                    <button className="btn-primary btn-large" onClick={() => setScreen('main')} disabled={!canStart}>
                      {t('startCollaborating') || 'Démarrer la collaboration'}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </button>
                    <button className="btn-secondary btn-large" onClick={() => { setScreen('main'); setTimeout(saveSession, 300); }} disabled={!canStart}>
                      🔗 {t('startSharedSession') || 'Démarrer une session partagée'}
                    </button>
                    <p className="setup-actions-hint">{t('sharedSessionHint') || 'Les sessions partagées permettent à d\'autres personnes de suivre et collaborer en temps réel.'}</p>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="tab-panel settings-panel">
              <h3>⚙️ {t('settings')}</h3>
              <div className="settings-subtabs">
                {[
                  'sound',
                   'voiceSelection',
                  'agents',
                  'interface',
                  ...(isAdmin || isSuperAdmin ? ['trialBanner'] : [])
                ].map(sub => (
                  <button
                    key={sub}
                    className={`settings-subtab ${settingsSubTab === sub ? 'active' : ''}`}
                    onClick={() => setSettingsSubTab(sub)}
                  >
                    {sub === 'sound' && (t('soundSettings') || 'Sound Settings')}
                    {sub === 'agents' && (t('configureAgents') || 'Configure Agents')}
                     {sub === 'voiceSelection' && (t('voiceSelection') || 'Voice Selection')}
                    {sub === 'interface' && (t('interfaceSettings') || 'Interface')}
                    {sub === 'trialBanner' && ('🚩 ' + (t('trialBanner') || 'Trial Banner'))}
                  </button>
                ))}
              </div>
              
              {settingsSubTab === 'sound' && (
                <div className="settings-subpanel">
                  <SoundSettings
                    isAdmin={isAdmin}
                    isSuperAdmin={isSuperAdmin}
                    elevenLabsEnabled={elevenLabsEnabled}
                    elevenLabsLoading={elevenLabsLoading}
                    toggleElevenLabs={toggleElevenLabs}
                  />
                </div>
              )}

              
               {settingsSubTab === 'voiceSelection' && (
                 <div className="settings-subpanel">
                   <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>}>
                     <VoiceSelector isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />
                   </Suspense>
                 </div>
               )}
              {settingsSubTab === 'agents' && (
                <div className="settings-subpanel agents-config-panel">
                  <div className="settings-section providers-section">
                    <h3>🔌 {t('checkingProviders')}</h3>
                    <div className={`setup-step provider-status-step ${setupLoading ? '' : connectedOk ? 'success' : 'error'}`}>
                      <div className="step-content">
                        <p>{setupLoading ? t('connecting') : connectedOk ? `${t('connectedTo')}: ${[hasLocal && 'Ollama', providers.claude?.configured && 'Claude', providers.kimi?.configured && 'Kimi', providers.openrouter?.configured && 'OpenRouter', providers.hf?.configured && 'HF', providers.tchadgpt?.configured && 'TchadGPT', providers.zai?.configured && 'Z.ai', providers.deepseek?.configured && 'DeepSeek', providers.xai?.configured && 'Grok', providers.chatlyai?.configured && 'ChatlyAI'].filter(Boolean).join(', ')}` : t('noProvidersAvailable')}</p>
                      </div>
                      <div className={`step-status ${setupLoading ? 'loading' : connectedOk ? 'success' : 'error'}`}>
                        {setupLoading ? '' : connectedOk ? '✓' : '!'}
                      </div>
                    </div>
                  </div>

                  <div className="settings-section">
                    <h3>🎯 {t('configureAgents')}</h3>

                    {isPowerUser ? (
                      <div className="api-keys-section">
                        <h4>☁️ {t('apiKeys')} <span className="optional-badge">{t('powerUser')}</span></h4>
                        <p className="section-desc">{t('addKeysDesc')}</p>
                        {['claude', 'kimi', 'openrouter', 'hf', 'tchadgpt', 'zai', 'deepseek', 'xai', 'chatlyai'].map(p => (
                          <ApiKeyRow key={p} provider={p} configured={providers[p]?.configured} validateKey={validateKey} />
                        ))}
                      </div>
                    ) : (
                      <div className="api-keys-section api-keys-locked">
                        <h4>☁️ {t('apiKeys')} <span className="optional-badge locked">{t('locked')}</span></h4>
                        <p className="section-desc">{t('powerUserFeature')}</p>
                        {!showUnlock ? (
                          <button className="btn-secondary btn-small" onClick={() => setShowUnlock(true)}>
                            🔓 {t('unlockPowerUser')}
                          </button>
                        ) : (
                          <div className="unlock-row">
                            <input
                              type="password"
                              placeholder={t('enterUnlockCode')}
                              value={unlockInput}
                              onChange={e => setUnlockInput(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && handleUnlock()}
                            />
                            <button className="btn-primary btn-small" onClick={handleUnlock} aria-label={t('unlock')}>{t('unlock')}</button>
                          </div>
                        )}
                        {unlockError && <p className="unlock-error">{t('incorrectUnlockCode')}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {settingsSubTab === 'trialBanner' && (
                <div className="settings-subpanel admin-banner-panel">
                  <h4>🚩 {t('trialBanner') || 'Trial Banner'}</h4>
                  <p className="section-desc">{t('trialBannerDesc') || 'Set a top-screen banner for users in trial period.'}</p>

                  <div className="field-group admin-toggle-row">
                    <div className="toggle-text">
                      <span className="toggle-title">{t('enableBanner') || 'Enable banner'}</span>
                      <span className="toggle-caption">{t('enableBannerDesc') || 'Show the banner to selected trial users.'}</span>
                    </div>
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={bannerConfig.enabled}
                        onChange={e => setBannerConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                        aria-label={t('enableBanner') || 'Enable banner'}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  <div className="field-group">
                    <label>{t('bannerMessage') || 'Banner message'}</label>
                    <textarea
                      value={bannerConfig.message}
                      onChange={e => setBannerConfig(prev => ({ ...prev, message: e.target.value }))}
                      rows={3}
                      placeholder={t('bannerMessagePlaceholder') || 'e.g. Your trial expires soon — subscribe now to keep access.'}
                    />
                    <span className="field-hint">{t('bannerMessageHint') || 'HTML is allowed (links, bold, etc.).'}</span>
                  </div>

                  <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="field-group">
                      <label>{t('backgroundColor') || 'Background color'}</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="color"
                          value={bannerConfig.backgroundColor}
                          onChange={e => setBannerConfig(prev => ({ ...prev, backgroundColor: e.target.value }))}
                          aria-label={t('backgroundColor') || 'Background color'}
                        />
                        <input
                          type="text"
                          value={bannerConfig.backgroundColor}
                          onChange={e => setBannerConfig(prev => ({ ...prev, backgroundColor: e.target.value }))}
                          style={{ flex: 1, minWidth: '80px' }}
                        />
                      </div>
                    </div>
                    <div className="field-group">
                      <label>{t('textColor') || 'Text color'}</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="color"
                          value={bannerConfig.textColor}
                          onChange={e => setBannerConfig(prev => ({ ...prev, textColor: e.target.value }))}
                          aria-label={t('textColor') || 'Text color'}
                        />
                        <input
                          type="text"
                          value={bannerConfig.textColor}
                          onChange={e => setBannerConfig(prev => ({ ...prev, textColor: e.target.value }))}
                          style={{ flex: 1, minWidth: '80px' }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="field-group">
                    <label>{t('showBannerFor') || 'Show banner for'}</label>
                    <div className="checkbox-row" style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                      <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={bannerConfig.showWhenExpired}
                          onChange={e => setBannerConfig(prev => ({ ...prev, showWhenExpired: e.target.checked }))}
                        />
                        {t('expiredTrialUsers') || 'Expired trial users'}
                      </label>
                      <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={bannerConfig.showWhenActive}
                          onChange={e => setBannerConfig(prev => ({ ...prev, showWhenActive: e.target.checked }))}
                        />
                        {t('activeTrialUsers') || 'Active trial users'}
                      </label>
                    </div>
                  </div>

                  <div className="banner-preview-box" style={{ marginTop: '16px', padding: '12px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-hover)' }}>
                    <label style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px', display: 'block' }}>{t('preview') || 'Preview'}</label>
                    <div
                      style={{
                        padding: '10px 16px',
                        borderRadius: '8px',
                        textAlign: 'center',
                        fontWeight: 600,
                        backgroundColor: bannerConfig.backgroundColor,
                        color: bannerConfig.textColor
                      }}
                      dangerouslySetInnerHTML={{ __html: bannerConfig.message || (t('bannerPreviewPlaceholder') || 'Your banner will appear here.') }}
                    />
                  </div>

                  <div className="banner-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
                    <button className="btn-primary" onClick={saveBannerConfig} disabled={bannerLoading}>
                      {bannerLoading ? (t('saving') || 'Saving...') : (t('saveBanner') || 'Save Banner')}
                    </button>
                    {bannerMessage && <span className="sound-settings-message">{bannerMessage}</span>}
                  </div>
                </div>
              )}

              {settingsSubTab === 'interface' && (
                <div className="settings-subpanel interface-settings-panel">
                  <h4>🖥️ {t('interfaceSettings') || 'Interface'}</h4>
                  <p className="section-desc">{t('interfaceSettingsDesc') || 'Personnalisez l\'apparence de l\'application et les options de partage.'}</p>

                  <div className="interface-theme-section" style={{ marginTop: '20px' }}>
                    <h5 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>🎨 {t('themes')}</h5>
                    <p className="section-desc" style={{ marginBottom: '12px' }}>{t('themesIntro')}</p>
                    <div className="theme-grid">
                      {Object.entries(THEMES).map(([key, theme]) => (
                        <button
                          key={key}
                          className={`theme-thumb ${currentTheme === key ? 'active' : ''}`}
                          onClick={() => handleThemeSelect(key)}
                          title={getThemeName(theme)}
                          style={{
                            backgroundImage: theme.image || undefined,
                            backgroundColor: theme.image ? undefined : theme.bg
                          }}
                        >
                          {!theme.image && (
                            <div className="theme-color-preview" style={{ background: `linear-gradient(135deg, ${theme.bg} 50%, ${theme.accent} 50%)` }} />
                          )}
                          <span className="theme-label">{getThemeName(theme)}</span>
                        </button>
                      ))}
                    </div>

                    <div className="theme-transparency-toggle" style={{ marginTop: '16px' }}>
                      <label className="toggle-label">
                        <span>{t('chatTransparency')}</span>
                        <input type="checkbox" checked={chatTransparent} onChange={handleToggleTransparency} />
                        <span className="toggle-slider"></span>
                      </label>
                      <p className="toggle-hint">{t('chatTransparencyHint')}</p>
                    </div>
                  </div>

                  <div className="interface-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid var(--border)' }}>
                    <button className="btn-ghost btn-small" onClick={() => setShowInvite(true)} aria-label={t('share')}>🔗 {t('share')}</button>
                    <button className="btn-ghost btn-small" onClick={toggleFullscreen} aria-label={t('fullscreen') || 'Plein écran'}>⛶ {t('fullscreen') || 'Plein écran'}</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'accessibility' && (
            <div className="tab-panel accessibility-panel">
              <AccessibilitySettings />
            </div>
          )}

          {activeTab === 'admin' && isAdmin && (
            <div className="tab-panel admin-panel-tab">
              <h3>👤 {t('adminPanel')}</h3>
              <div className="admin-subtabs">
                 {(isSuperAdmin ? ['users', 'modelGrowth', 'connectionLog'] : ['users']).map(sub => (
                  <button
                    key={sub}
                    className={`admin-subtab ${adminSubTab === sub ? 'active' : ''}`}
                    onClick={() => { setAdminSubTab(sub); if (sub === 'modelGrowth') fetchModelGrowth(); }}
                  >
                    {sub === 'users' && '👥 ' + t('userManagement')}
                    {sub === 'modelGrowth' && '📈 ' + t('modelGrowth')}
                    {sub === 'notify' && '🔔 ' + t('notifyUsers')}
                    {sub === 'connectionLog' && '📜 ' + t('connectionLog')}
                  </button>
                ))}
              </div>

              {adminSubTab === 'users' && (
                <div className="admin-users-panel">
                  {adminLoading ? (
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('loading')}</p>
                  ) : (
                    <>
                      {allUsers.filter(u => u.role === 'pending').length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{ fontSize: '14px', marginBottom: '10px', color: 'var(--text-secondary)' }}>{t('pendingApproval')}</h4>
                          {allUsers.filter(u => u.role === 'pending').map(u => (
                            <div key={u.id} className="user-row">
                              <span>{u.name} ({u.email})</span>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <select
                                  className="btn-small"
                                  style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '12px' }}
                                  value={pendingRoles[u.id] || 'subscriber'}
                                  onChange={e => setPendingRoles(prev => ({ ...prev, [u.id]: e.target.value }))}
                                >
                                  <option value="subscriber">Subscriber</option>
                                  <option value="admin">Admin</option>
                                  <option value="moderator">Moderator</option>
                                  <option value="reporter">Reporter</option>
                                  <option value="analyser">Analyser</option>
                                </select>
                                <button className="btn-primary btn-small" onClick={() => verifyUser(u.id, pendingRoles[u.id] || 'subscriber')}>{t('verify')}</button>
                                <button className="btn-secondary btn-small" onClick={() => rejectUser(u.id)}>{t('reject')}</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                       <h4 style={{ fontSize: '14px', marginBottom: '10px', color: 'var(--text-secondary)' }}>{t('allUsers')} ({allUsers.length})</h4>
                       <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                         <span style={{ fontSize: '12px', color: 'var(--text-muted)', alignSelf: 'center' }}>Sort by:</span>
                         {[
                           { key: 'name', label: 'Name' },
                           { key: 'date', label: 'Date' },
                           { key: 'status', label: 'Status' }
                         ].map(s => (
                           <button
                             key={s.key}
                             className={`btn-small ${userSort === s.key ? 'btn-primary' : 'btn-secondary'}`}
                             onClick={() => setUserSort(s.key)}
                           >
                             {s.label}
                           </button>
                         ))}
                       </div>
                       {allUsers.length === 0 ? (
                         <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('noUsersFound')}</p>
                       ) : (
                         <div className="admin-user-list">
                           {[...allUsers].sort((a, b) => {
                             if (userSort === 'name') return (a.name || '').localeCompare(b.name || '');
                             if (userSort === 'date') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                             if (userSort === 'status') return (a.status || 'active').localeCompare(b.status || 'active');
                             return 0;
                           }).map(u => (
                            <div key={u.id} className="user-row admin-user-row">
                              <div className="user-info">
                                <span className="user-name">{u.name}</span>
                                <span className="user-email">{u.email}</span>
                                <span className={`user-status-badge ${u.status || 'active'}`}>{u.status === 'suspended' ? t('suspended') : t('active')}</span>
                              </div>
                              <div className="user-actions">
                                {isSuperAdmin && <RoleSelect user={u} onChange={(role) => changeRole(u.id, role)} />}
                                {isSuperAdmin && (
                                  <>
                                    <select
                                      value={actionSelect[u.id] || ''}
                                      onChange={e => setActionSelect(prev => ({ ...prev, [u.id]: e.target.value }))}
                                      style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '12px', minWidth: '100px' }}
                                    >
                                      <option value="">— Action —</option>
                                      <option value="edit">{t('edit') || 'Éditer'}</option>
                                      <option value="suspend">{u.status === 'suspended' ? (t('activateUser') || 'Activer') : (t('suspendUser') || 'Suspendre')}</option>
                                      <option value="password">{t('resetPassword') || 'Mot de passe'}</option>
                                      <option value="eject">{t('ejectUser') || 'Éjecter'}</option>
                                      <option value="delete">{t('deleteUser') || 'Supprimer'}</option>
                                      <option value="notify">{t('notifyUser') || 'Notifier'}</option>
                                      <option value="banner">{t('applyBanner') || 'Bannière'}</option>
                                    </select>
                                    <button
                                      className="btn-primary btn-small"
                                      disabled={!actionSelect[u.id]}
                                      onClick={() => {
                                        const action = actionSelect[u.id];
                                        if (!action) return;
                                        setActionSelect(prev => ({ ...prev, [u.id]: '' }));
                                        switch (action) {
                                          case 'edit':
                                            openEditUser(u);
                                            break;
                                          case 'suspend':
                                            u.status === 'suspended' ? activateUser(u.id) : suspendUser(u.id);
                                            break;
                                          case 'password':
                                            setResetUserId(u.id); setResetPasswordValue(''); setResetConfirmPassword(''); setResetError(''); setResetSuccess('');
                                            break;
                                          case 'eject':
                                            ejectUser(u.id);
                                            break;
                                          case 'delete':
                                            deleteUser(u.id);
                                            break;
                                          case 'notify':
                                            notifyUser(u.id);
                                            break;
                                          case 'banner':
                                            applyBannerToUser(u.id);
                                            break;
                                        }
                                      }}
                                    >{t('execute') || 'Exécuter'}</button>
                                  </>
                                )}
                              </div>
                              {editUserId === u.id && (
                                <div className="admin-inline-form">
                                  <input value={editName} onChange={e => setEditName(e.target.value)} placeholder={t('name') || 'Nom'} style={{ flex: '1 1 160px', minWidth: '120px', maxWidth: '240px' }} />
                                  <input value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder={t('email') || 'Email'} style={{ flex: '2 1 220px', minWidth: '160px', maxWidth: '320px' }} />
                                  <div className="inline-form-actions">
                                    <button className="btn-primary btn-small" onClick={() => editUser(u.id)} disabled={editLoading}>
                                      {editLoading ? '...' : (t('save') || 'Enregistrer')}
                                    </button>
                                    <button className="btn-secondary btn-small" onClick={() => { setEditUserId(null); setEditName(''); setEditEmail(''); }}>
                                      {t('cancel') || 'Annuler'}
                                    </button>
                                  </div>
                                  {editError && <div className="form-msg error">{editError}</div>}
                                  {editSuccess && <div className="form-msg success">{editSuccess}</div>}
                                </div>
                              )}
                              {resetUserId === u.id && (
                                <div className="admin-inline-form">
                                  <input type="password" placeholder={t('newPassword')} value={resetPasswordValue} onChange={e => setResetPasswordValue(e.target.value)} style={{ flex: '1 1 160px', minWidth: '120px', maxWidth: '240px' }} />
                                  <input type="password" placeholder={t('confirmPassword')} value={resetConfirmPassword} onChange={e => setResetConfirmPassword(e.target.value)} style={{ flex: '1 1 160px', minWidth: '120px', maxWidth: '240px' }} />
                                  <div className="inline-form-actions">
                                    <button className="btn-primary btn-small" onClick={() => resetPassword(u.id)}>{t('save')}</button>
                                    <button className="btn-secondary btn-small" onClick={() => setResetUserId(null)}>{t('cancel')}</button>
                                  </div>
                                  {resetError && <div className="form-msg error">{resetError}</div>}
                                  {resetSuccess && <div className="form-msg success">{resetSuccess}</div>}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {adminSubTab === 'modelGrowth' && (
                <div className="admin-model-growth">
                  <h4>📈 {t('localModelStats')}</h4>
                  {growthLoading ? (
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('loading')}</p>
                  ) : !modelGrowth ? (
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('noData')}</p>
                  ) : (
                    <>
                      <div className="growth-stats-grid">
                        <div className="growth-stat-card">
                          <div className="growth-stat-value">{modelGrowth.totalLocalCalls}</div>
                          <div className="growth-stat-label">{t('totalLocalCalls')}</div>
                        </div>
                        <div className="growth-stat-card">
                          <div className="growth-stat-value">{(modelGrowth.totalTokensIn + modelGrowth.totalTokensOut).toLocaleString()}</div>
                          <div className="growth-stat-label">{t('totalTokens')}</div>
                        </div>
                        <div className="growth-stat-card">
                          <div className="growth-stat-value">{modelGrowth.sessionsWithLocal}</div>
                          <div className="growth-stat-label">{t('sessionsWithLocal')}</div>
                        </div>
                        <div className="growth-stat-card">
                          <div className="growth-stat-value">{modelGrowth.learningScore}%</div>
                          <div className="growth-stat-label">{t('learningScore')}</div>
                        </div>
                      </div>

                      {modelGrowth.modelCounts?.length > 0 && (
                        <div className="growth-section">
                          <h5>{t('modelUsage')}</h5>
                          <div className="model-usage-list">
                            {modelGrowth.modelCounts.map(m => (
                              <div key={m.model} className="model-usage-row">
                                <span className="model-name">{m.model}</span>
                                <div className="model-bar-bg">
                                  <div className="model-bar-fill" style={{ width: `${Math.min(100, (m.calls / modelGrowth.totalLocalCalls) * 100)}%` }}></div>
                                </div>
                                <span className="model-calls">{m.calls}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {modelGrowth.dailyGrowth?.length > 0 && (
                        <div className="growth-section">
                          <h5>{t('dailyGrowth')}</h5>
                          <div className="daily-growth-chart">
                            {modelGrowth.dailyGrowth.map(d => (
                              <div key={d.date} className="daily-growth-bar-wrapper">
                                <div className="daily-growth-bar" style={{ height: `${Math.min(100, Math.max(4, (d.tokens / Math.max(...modelGrowth.dailyGrowth.map(x => x.tokens))) * 80))}px` }}></div>
                                <span className="daily-growth-label">{d.date.slice(5)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {adminSubTab === 'notify' && (
                <div className="admin-notify-panel">
                  <h4>🔔 {t('notifyUsers')}</h4>
                  <div className="notify-form">
                    <div className="field-group">
                      <label>{t('selectUsers')}</label>
                      <select multiple value={notifyUserIds} onChange={e => {
                        const opts = Array.from(e.target.selectedOptions).map(o => o.value);
                        setNotifyUserIds(opts);
                      }} style={{ minHeight: '120px' }}>
                        <option value="all">🌐 {t('all')}</option>
                        {allUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                        ))}
                      </select>
                    </div>
                    <div className="field-group">
                      <label>{t('notificationTitle')}</label>
                      <input type="text" value={notifyTitle} onChange={e => setNotifyTitle(e.target.value)} placeholder={t('notificationTitle')} />
                    </div>
                    <div className="field-group">
                      <label>{t('notificationMessage')}</label>
                      <textarea value={notifyMessage} onChange={e => setNotifyMessage(e.target.value)} rows={4} placeholder={t('notificationMessage')} />
                    </div>
                    <button className="btn-primary" onClick={sendNotification} disabled={notifySending || !notifyMessage.trim()}>
                      {notifySending ? t('sending') : t('sendNotification')}
                    </button>
                  </div>
                </div>
              )}

              {adminSubTab === 'connectionLog' && isSuperAdmin && (
                <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>}>
                  <ConnectionLogPanel />
                </Suspense>
              )}

            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="tab-panel pricing-panel-tab">
              <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>}>
                <PricingPanel currentPlan={user?.plan || 'free'} />
              </Suspense>
            </div>
          )}

          {activeTab === 'charter' && (
            <div className="tab-panel charter-panel-tab">
              <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>}>
                <CharterPanel />
              </Suspense>
            </div>
          )}

          {activeTab === 'financial' && isSuperAdmin && (
            <div className="tab-panel financial-panel">
              <div className="financial-header">
                <h3>💰 {t('financial') || 'Financial Overview'}</h3>
                <p className="section-desc">{t('financialDesc') || 'Token usage, costs in FCFA (XAF), and detailed analytics'}</p>
              </div>

              <div className="financial-controls" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                  <span>{t('period') || 'Period'}:</span>
                  <select value={financialDays} onChange={e => setFinancialDays(parseInt(e.target.value))} style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>
                    <option value={7}>7 {t('days') || 'days'}</option>
                    <option value={30}>30 {t('days') || 'days'}</option>
                    <option value={90}>90 {t('days') || 'days'}</option>
                    <option value={365}>1 {t('year') || 'year'}</option>
                  </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                  <span>{t('reportLanguage') || 'Langue du rapport'}:</span>
                  <select value={reportLanguage} onChange={e => setReportLanguage(e.target.value)} style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>
                    <option value="fr">🇫🇷 Français</option>
                    <option value="en">🇬🇧 English</option>
                    <option value="ar">🇸🇦 العربية</option>
                  </select>
                </label>
                <button className="btn-secondary btn-small" onClick={fetchFinancialData} disabled={financialLoading}>
                  🔄 {financialLoading ? (t('refreshing') || 'Refreshing...') : (t('refresh') || 'Refresh')}
                </button>
                <button id="export-pdf-btn" className="btn-primary btn-small" onClick={(e) => { 
                  e.preventDefault(); 
                  console.log('[PDF] Export button clicked');
                  exportFinancialPDF(); 
                }} disabled={!financialData}>
                  📄 {t('exportPDF') || 'Export PDF'}
                </button>
                <button className="btn-secondary btn-small" onClick={(e) => {
                  e.preventDefault();
                  openFinancialPDFPreview();
                }} disabled={!financialData || pdfPreviewLoading}>
                  {pdfPreviewLoading ? (t('loading') || 'Chargement...') : (t('preview') || 'Aperçu')}
                </button>
              </div>

              {financialLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                  {t('loadingFinancialData') || 'Loading financial data...'}
                </div>
              ) : financialData ? (
                <>
                  {/* Summary Cards */}
                  <div className="financial-summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    <div className="financial-card" style={{ background: 'var(--bg-hover)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border)', minHeight: '68px', height: 'fit-content', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('totalSessions') || 'Total Sessions'}</div>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--text-primary)', lineHeight: '1.2' }}>{financialData.total.calls.toLocaleString()}</div>
                    </div>
                    <div className="financial-card" style={{ background: 'var(--bg-hover)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border)', minHeight: '68px', height: 'fit-content', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('totalTokens') || 'Total Tokens'}</div>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--text-primary)', lineHeight: '1.2' }}>
                        {(financialData.total.tokensIn + financialData.total.tokensOut).toLocaleString()}
                      </div>
                    </div>
                    <div className="financial-card" style={{ background: 'var(--bg-hover)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border)', minHeight: '68px', height: 'fit-content', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('totalCost') || 'Total Cost'}</div>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--text-primary)', lineHeight: '1.2' }}>
                        {formatPriceUSD(financialData.total.cost).amount}
                      </div>
                    </div>
                    <div className="financial-card" style={{ background: 'var(--bg-hover)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border)', minHeight: '68px', height: 'fit-content', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>{t('totalCostLocal') || 'Total Cost (Local)'}</div>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981', lineHeight: '1.2' }}>
                        {formatPriceUSD(financialData.total.cost).amount} {currency !== 'XOF' && currency !== 'FCFA' && `(${symbol})`}
                      </div>
                    </div>
                  </div>

                  {/* Tokens by Session */}
                  <div className="financial-section" style={{ marginBottom: '24px' }}>
                    <h4>📊 {t('tokenUsageBySession') || 'Token Usage by Session'}</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="financial-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border)' }}>
                            <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('provider') || 'Provider'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('calls') || 'Calls'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('tokensIn') || 'Tokens In'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('tokensOut') || 'Tokens Out'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('totalTokens') || 'Total'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('cost') || 'Cost'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('costLocal') || 'Cost (Local)'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financialData.byProvider.map(provider => {
                            const totalTokens = provider.tokensIn + provider.tokensOut;
                            return (
                              <tr key={provider.provider} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>
                                  <strong>{provider.provider}</strong>
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{provider.calls.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{provider.tokensIn.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{provider.tokensOut.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{totalTokens.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>
                                  {formatPriceUSD(provider.cost).amount}
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px', color: '#10b981' }}>
                                  {formatPriceUSD(provider.cost).amount}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Costs by Agent / Model */}
                  <div className="financial-section" style={{ marginBottom: '24px' }}>
                    <h4>🤖 {t('costsByAgent') || 'Coûts par Agent / Modèle'}</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="financial-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border)' }}>
                            <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('agent') || 'Agent'}</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('provider') || 'Provider'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('calls') || 'Calls'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('tokensIn') || 'Tokens In'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('tokensOut') || 'Tokens Out'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('cost') || 'Cost'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financialData.byAgent?.map(agent => {
                            const totalTokens = agent.tokensIn + agent.tokensOut;
                            return (
                              <tr key={agent.agentId} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: 600 }}>{agent.agentId}</td>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>{agent.provider}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{agent.calls.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{agent.tokensIn.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{agent.tokensOut.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px', color: '#10b981' }}>{formatPriceUSD(agent.cost).amount}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Session Cost Estimate */}
                  {financialData.sessionEstimate && (
                    <div className="financial-section" style={{ marginBottom: '24px' }}>
                      <h4>💼 {t('sessionCosts') || 'Coût des sessions'}</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table className="financial-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--border)' }}>
                              <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('metric') || 'Métrique'}</th>
                              <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('value') || 'Valeur'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>{t('estimatedSessions') || 'Sessions estimées'}</td>
                              <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{financialData.sessionEstimate.count.toLocaleString()}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>{t('averageCostPerSession') || 'Coût moyen par session'}</td>
                              <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px', color: '#10b981' }}>{formatPriceUSD(financialData.sessionEstimate.averageCost).amount}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>{t('averageTokensInPerSession') || 'Tokens entrée moyens / session'}</td>
                              <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{Math.round(financialData.sessionEstimate.averageTokensIn).toLocaleString()}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>{t('averageTokensOutPerSession') || 'Tokens sortie moyens / session'}</td>
                              <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{Math.round(financialData.sessionEstimate.averageTokensOut).toLocaleString()}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Tokens by User */}
                  <div className="financial-section" style={{ marginBottom: '24px' }}>
                    <h4>👥 {t('tokenUsageByUser') || 'Token Usage by User'}</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="financial-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border)' }}>
                            <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('user') || 'User'}</th>
                            <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('plan') || 'Plan'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('calls') || 'Calls'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('tokensIn') || 'Tokens In'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('tokensOut') || 'Tokens Out'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('totalTokens') || 'Total'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('cost') || 'Cost'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financialData.byUser.map(user => {
                            const totalTokens = user.tokensIn + user.tokensOut;
                            return (
                              <tr key={user.userId} style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>
                                  {user.name || user.email || user.userId}
                                </td>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px' }}>
                                  <span style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>
                                    {user.plan || 'free'}
                                  </span>
                                </td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{user.calls.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{user.tokensIn.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{user.tokensOut.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>{totalTokens.toLocaleString()}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px', color: '#10b981' }}>
                                  {formatPriceUSD(user.cost).amount}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pricing Rates */}
                  <div className="financial-section" style={{ marginBottom: '24px' }}>
                    <h4>💵 {t('pricingRates') || 'Pricing Rates (per 1M tokens)'}</h4>
                    <div style={{ overflowX: 'auto', marginTop: '12px' }}>
                      <table className="financial-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--border)' }}>
                            <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('provider') || 'Provider'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('costInput') || 'Input'}</th>
                            <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('costOutput') || 'Output'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(financialData.rates).map(([provider, rates]) => (
                            <tr key={provider} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: 600 }}>{provider}</td>
                              <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>
                                {formatPriceUSD(rates.in).amount} / 1M
                              </td>
                              <td style={{ textAlign: 'right', padding: '12px', fontSize: '14px' }}>
                                {formatPriceUSD(rates.out).amount} / 1M
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      {t('displayingIn') || 'Displaying costs in'}: {symbol} ({currency})
                    </div>
                  </div>

                  {/* Service Costs */}
                  {financialData.serviceCosts && (
                    <div className="financial-section" style={{ marginBottom: '24px' }}>
                      <h4>🛠️ {t('serviceCosts') || 'Coûts des services'}</h4>
                      <div style={{ overflowX: 'auto', marginTop: '12px' }}>
                        <table className="financial-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--border)' }}>
                              <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('service') || 'Service'}</th>
                              <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('description') || 'Description'}</th>
                              <th style={{ textAlign: 'right', padding: '12px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)' }}>{t('cost') || 'Coût'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {financialData.serviceCosts.elevenlabs && (
                              <tr key="elevenlabs" style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: 600 }}>🎙️ ElevenLabs</td>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '13px' }}>TTS / STT</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '13px' }}>
                                  <div>🔊 {formatPriceUSD(financialData.serviceCosts.elevenlabs.tts.costPerChar * 1000000).amount} / 1M chars</div>
                                  <div>🎤 {formatPriceUSD(financialData.serviceCosts.elevenlabs.stt.costPerMinute).amount} / min</div>
                                </td>
                              </tr>
                            )}
                            {financialData.serviceCosts.voiceServices && (
                              <tr key="voiceServices" style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: 600 }}>🎤 {t('voiceServices') || 'Services vocaux'}</td>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '13px' }}>Groq Whisper STT, Mistral TTS/STT</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '13px' }}>
                                  <div>{formatPriceUSD(financialData.serviceCosts.voiceServices.groqStt.costPerMinute).amount} / min STT (Groq)</div>
                                  <div>{formatPriceUSD(financialData.serviceCosts.voiceServices.mistralTts.costPerChar * 1000000).amount} / 1M chars TTS (Mistral)</div>
                                  <div>{formatPriceUSD(financialData.serviceCosts.voiceServices.mistralStt.costPerMinute).amount} / min STT (Mistral)</div>
                                </td>
                              </tr>
                            )}
                            {financialData.serviceCosts.imageGeneration && (
                              <tr key="imageGeneration" style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: 600 }}>🎨 {t('imageGeneration') || 'Génération d\'images'}</td>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '13px' }}>{financialData.serviceCosts.imageGeneration.description}</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '13px' }}>
                                  {formatPriceUSD(financialData.serviceCosts.imageGeneration.costPerImage.low).amount} - {formatPriceUSD(financialData.serviceCosts.imageGeneration.costPerImage.high).amount} / image
                                </td>
                              </tr>
                            )}
                            {financialData.serviceCosts.documentGeneration && (
                              <tr key="documentGeneration" style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: 600 }}>📄 {t('documentGeneration') || 'Génération de documents'}</td>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '13px' }}>Export PDF, PPTX, DOCX</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '13px' }}>
                                  <div>PDF: {formatPriceUSD(financialData.serviceCosts.documentGeneration.pdf.costPerDocument).amount} / doc</div>
                                  <div>PPTX: {formatPriceUSD(financialData.serviceCosts.documentGeneration.pptx.costPerDocument).amount} / doc</div>
                                  <div>DOCX: {formatPriceUSD(financialData.serviceCosts.documentGeneration.docx.costPerDocument).amount} / doc</div>
                                </td>
                              </tr>
                            )}
                            {financialData.serviceCosts.diagramGeneration && (
                              <tr key="diagramGeneration" style={{ borderBottom: '1px solid var(--border)' }}>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '14px', fontWeight: 600 }}>🧠 {t('diagramGeneration') || 'Génération de schémas'}</td>
                                <td style={{ textAlign: 'left', padding: '12px', fontSize: '13px' }}>Mind maps, schémas et diagrammes</td>
                                <td style={{ textAlign: 'right', padding: '12px', fontSize: '13px' }}>
                                  <div>Mind map: {formatPriceUSD(financialData.serviceCosts.diagramGeneration.mindMap.costPerMap).amount} / carte</div>
                                  <div>Schéma: {formatPriceUSD(financialData.serviceCosts.diagramGeneration.schema.costPerSchema).amount} / schéma</div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>                    </div>
                  )}

                  <div style={{ marginTop: '24px', fontSize: '12px', color: 'var(--text-muted)' }}>
                    {t('dataPeriod') || 'Data period'}: {financialData.days} {t('days') || 'days'}
                  </div>

                  {pdfPreviewOpen && (
                    <div className="pdf-preview-modal" style={{
                      position: 'fixed',
                      top: 0, left: 0, right: 0, bottom: 0,
                      background: 'rgba(0,0,0,0.75)',
                      zIndex: 9999,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px'
                    }}>
                      <div style={{
                        background: 'var(--bg-card)',
                        borderRadius: '12px',
                        width: '100%',
                        maxWidth: '900px',
                        height: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '16px 20px',
                          borderBottom: '1px solid var(--border)'
                        }}>
                          <h3 style={{ margin: 0, fontSize: '16px' }}>{t('pdfPreview') || 'Aperçu du rapport financier'}</h3>
                          <button
                            onClick={closeFinancialPDFPreview}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              fontSize: '24px',
                              cursor: 'pointer',
                              color: 'var(--text)',
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                        </div>
                        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                          {pdfPreviewLoading ? (
                            <div style={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              gap: '16px',
                              color: 'var(--text-muted)'
                            }}>
                              <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                              {t('loading') || 'Chargement...'}
                            </div>
                          ) : pdfPreviewUrl ? (
                            <iframe
                              src={pdfPreviewUrl}
                              title={t('pdfPreview') || 'Aperçu PDF'}
                              style={{ width: '100%', height: '100%', border: 'none' }}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  {t('noFinancialData') || 'No financial data available'}
                </div>
              )}
            </div>
          )}
        </>
        )}
        </div>
      </div>

      {showChangePassword && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '420px', color: 'var(--text-primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>{t('changePassword') || 'Changer le mot de passe'}</h3>
              <button className="btn-ghost btn-small" onClick={() => setShowChangePassword(false)}>✕</button>
            </div>
            <input
              type="password"
              placeholder={t('currentPassword') || 'Mot de passe actuel'}
              value={changePasswordCurrent}
              onChange={(e) => setChangePasswordCurrent(e.target.value)}
              className="setup-input"
              style={{ width: '100%', marginBottom: '0.75rem' }}
            />
            <input
              type="password"
              placeholder={t('newPassword') || 'Nouveau mot de passe'}
              value={changePasswordNew}
              onChange={(e) => setChangePasswordNew(e.target.value)}
              className="setup-input"
              style={{ width: '100%', marginBottom: '0.75rem' }}
            />
            <input
              type="password"
              placeholder={t('confirmPassword') || 'Confirmer le mot de passe'}
              value={changePasswordConfirm}
              onChange={(e) => setChangePasswordConfirm(e.target.value)}
              className="setup-input"
              style={{ width: '100%', marginBottom: '0.75rem' }}
            />
            {changePasswordError && <p style={{ color: '#ef4444', fontSize: '13px', marginBottom: '0.75rem' }}>{changePasswordError}</p>}
            {changePasswordSuccess && <p style={{ color: '#22c55e', fontSize: '13px', marginBottom: '0.75rem' }}>{changePasswordSuccess}</p>}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="btn-secondary btn-small" onClick={() => setShowChangePassword(false)}>{t('cancel') || 'Annuler'}</button>
              <button className="btn-primary btn-small" onClick={changePassword}>{t('save') || 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

function RoleSelect({ user, onChange }) {
  const roles = ['admin', 'moderator', 'subscriber', 'reporter', 'analyser'];
  const [value, setValue] = useState(user.role);

  const handleChange = (e) => {
    const newRole = e.target.value;
    setValue(newRole);
    onChange(newRole);
  };

  return (
    <select
      value={value}
      onChange={handleChange}
      style={{
        padding: '4px 8px',
        fontSize: '12px',
        borderRadius: '6px',
        border: '1px solid var(--border)',
        background: 'var(--bg-card)',
        color: 'var(--text-primary)',
        cursor: 'pointer'
      }}
    >
      {roles.map(r => (
        <option key={r} value={r}>{r}</option>
      ))}
    </select>
  );
}

function ApiKeyRow({ provider, configured, validateKey }) {
  const [status, setStatus] = useState(configured ? 'valid' : '');
  const labels = { claude: 'Anthropic (Claude)', kimi: 'Moonshot (Kimi)', openrouter: 'OpenRouter', hf: 'Hugging Face', tchadgpt: 'TchadGPT', zai: 'Z.ai (GLM)', deepseek: 'DeepSeek', xai: 'xAI (Grok)', chatlyai: 'ChatlyAI' };
  const dots = { claude: '#d946ef', kimi: '#f59e0b', openrouter: '#10b981', hf: '#f97316', tchadgpt: '#002664', zai: '#3b82f6', deepseek: '#4f46e5', xai: '#f97316', chatlyai: '#ec4899' };
  const placeholders = { claude: 'sk-ant-api03-...', kimi: 'sk-...', openrouter: 'sk-or-v1-...', hf: 'hf_...', tchadgpt: 'tchad_...', zai: 'c6a0e4...', deepseek: 'sk-d06a...', xai: 'xai-...', chatlyai: 'chatly_...' };

  useEffect(() => {
    setStatus(configured ? 'valid' : '');
  }, [configured]);

  const onBlur = async (e) => {
    const key = e.target.value.trim();
    if (!key) { setStatus(''); return; }
    setStatus('checking');
    const valid = await validateKey(provider, key);
    setStatus(valid ? 'valid' : 'invalid');
  };

  return (
    <div className="api-key-row">
      <div className="api-key-label">
        <span className="provider-dot" style={{ background: dots[provider] }}></span>
        <span>{labels[provider]}</span>

      </div>
      <input type="password" placeholder={placeholders[provider]} onBlur={onBlur} />
      <span className={`key-status ${status}`}>
        {status === 'valid' ? '✓ valid' : status === 'invalid' ? '✗ invalid' : status === 'checking' ? 'checking...' : ''}
      </span>
    </div>
  );
}
